/**
 * Author: Andrey Morozov
 * Confidential. Proprietary and confidential. Unauthorized copying or distribution is prohibited.
 */

import { LocaleSwitcher, useTranslation } from "@iam/design-system/i18n";
import { useEffect, useState, type ReactNode } from "react";
import { coreLoginURL, fetchCoreSession, type CoreOIDCSession } from "./session";

/** Gate console children on a successful core OIDC session; redirects to login when missing. */
export function AuthGate({ children }: { children: (session: CoreOIDCSession) => ReactNode }) {
  const t = useTranslation();
  const [session, setSession] = useState<CoreOIDCSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (window.location.pathname === "/auth/callback") {
      window.history.replaceState({}, "", "/");
    }
    void fetchCoreSession()
      .then(setSession)
      .catch((cause) => setError(cause instanceof Error ? cause.message : "IAM.Identity login failed"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <main className="core-login" aria-busy="true">
        {t("auth.checking_session")}
      </main>
    );
  }
  if (session) return <>{children(session)}</>;

  return (
    <main className="core-login" data-testid="core-login">
      <section className="core-login__card">
        <div className="core-login__toolbar">
          <LocaleSwitcher />
        </div>
        <span className="core-login__eyebrow">IAM.Identity</span>
        <h1>IAM.Core</h1>
        <p>{t("auth.description")}</p>
        {error && <p className="core-login__error" role="alert">{error}</p>}
        <button type="button" onClick={() => window.location.assign(coreLoginURL())}>
          {t("auth.sign_in")}
        </button>
      </section>
    </main>
  );
}
